import { Button, Select, SelectItem, TextInput } from '@carbon/react';
import { Template } from '@carbon/icons-react';
import {
  asProblem,
  detailRoute,
  FormModal,
  hasPermission,
  isEmptyValue,
  problemMessage,
  requiredMarking,
  useAdmin,
  useMarkedLabel,
} from '@pos/admin-ui';
import { useTranslation } from '@pos/i18n';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import { hoApi } from '../api';
import { ErrorNotice } from '../common/Notices';
import { ListLoading } from '../common/Loading';
import './flows.scss';

/**
 * *Create from a preset* on the order flows list: pick one of the ready-made flows, name it, and open the new flow to
 * change its steps (FR-CFG-02, ADR 0024).
 */
export function FlowPresetToolbar() {
  const { t } = useTranslation();
  const { session, resources } = useAdmin();
  const flowDef = resources['config.order_flow'];
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [preset, setPreset] = useState('');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const label = useMarkedLabel();
  const presets = useQuery({
    queryKey: ['ho', 'order-flow-presets'],
    enabled: open,
    queryFn: async () => {
      const { data, error } = await hoApi.GET('/api/v1/menu/config/order-flow-presets');
      if (!data) throw error;
      return data;
    },
  });

  const create = useMutation({
    mutationFn: async () => {
      const { data, error } = await hoApi.POST('/api/v1/menu/config/order-flows/from-preset', {
        body: { presetCode: preset, code: code.trim(), name: name.trim() },
      });
      if (!data) throw error;
      return data;
    },
    onSuccess: (flow) => void navigate(detailRoute('config.order_flow', flow.id)),
  });

  if (!hasPermission(session, 'ho.config.order_flows.manage')) return null;
  const choose = (value: string) => {
    setPreset(value);
    const picked = presets.data?.find((p) => p.code === value);
    if (picked && !name) setName(t('flows.preset.name', { en: picked.name, vi: picked.nameVi }));
  };
  // All three fields are required, so none is marked (required-marking rule); each says so when left empty.
  const marking = requiredMarking([{ required: true }, { required: true }, { required: true }]);
  const mark = (text: string) => label(text, true, marking);
  const values = { preset, code: code.trim(), name: name.trim() };
  const invalid = (field: keyof typeof values) => (submitted || touched.has(field)) && isEmptyValue(values[field]);
  const touch = (field: string) => setTouched((current) => new Set(current).add(field));
  const requiredText = t('admin.validation.required');
  const close = () => {
    setOpen(false);
    setSubmitted(false);
    setTouched(new Set());
    create.reset();
  };

  return (
    <>
      <Button kind="ghost" renderIcon={Template} onClick={() => setOpen(true)}>
        {t('flows.fromPreset.button')}
      </Button>
      {open &&
        createPortal(
          <FormModal
            title={t('admin.create')}
            label={flowDef ? t(flowDef.title) : undefined}
            pending={create.isPending}
            error={create.error ? problemMessage(t, asProblem(create.error)) : null}
            onSubmit={() => {
              setSubmitted(true);
              if (Object.values(values).every((v) => !isEmptyValue(v))) create.mutate();
            }}
            onClose={close}
          >
            <Select
              id="new-flow-preset"
              labelText={mark(t('flows.fromPreset.preset'))}
              helperText={t('flows.fromPreset.body')}
              required
              value={preset}
              disabled={!presets.data}
              invalid={invalid('preset')}
              invalidText={requiredText}
              onBlur={() => touch('preset')}
              onChange={(e) => choose(e.target.value)}
            >
              <SelectItem value="" text={t('admin.choose')} />
              {(presets.data ?? []).map((p) => (
                <SelectItem key={p.code} value={p.code} text={t('flows.preset.name', { en: p.name, vi: p.nameVi })} />
              ))}
            </Select>
            {presets.isLoading && <ListLoading />}
            {presets.error && (
              <ErrorNotice title={problemMessage(t, asProblem(presets.error))} onRetry={() => void presets.refetch()} />
            )}
            <TextInput
              id="new-flow-code"
              labelText={mark(t('flows.fromPreset.code'))}
              required
              value={code}
              maxLength={50}
              invalid={invalid('code')}
              invalidText={requiredText}
              onBlur={() => touch('code')}
              onChange={(e) => setCode(e.target.value)}
            />
            <TextInput
              id="new-flow-name"
              labelText={mark(t('flows.fromPreset.name'))}
              required
              value={name}
              maxLength={200}
              invalid={invalid('name')}
              invalidText={requiredText}
              onBlur={() => touch('name')}
              onChange={(e) => setName(e.target.value)}
            />
          </FormModal>,
          document.body,
        )}
    </>
  );
}
